import { Email, PersonOutlined, Phone, PhoneAndroid } from "@mui/icons-material";
import { Box, Button, TextField, Typography } from "@mui/material";
import { observer } from "mobx-react";
import * as React from "react"
import useStores from "../../../hooks/use-stores";
import { useEffect, useState } from "react";
import VmButton from "../../shared-button";
import { LoginInfo, MFAType } from "../../../constants/options";
import { getContactInfoOptionText, GetErrorInfo, GetUserProfile } from "../../../utilities/general";
import { IsEmptyStr, IsNumeric, IsValidEmailAddress } from "../../../utilities/field-validation";

export enum ContactInfoOption { MOBILE_NUMBER, PHONE_NUMBER, EMAIL_ADDRESS };
enum PageStep { SEND_CURRENT, CHECK_CURRENT, ENTER_NEW_CONTACT_INFO, VERIFY_NEW_CONTACT_INFO, UPDATE_FINISHED };

const UpdateContactInfoModal = observer(({ mode, onClose }: { mode: ContactInfoOption, onClose?: () => any }) => {
  const { userStore, rootStore } = useStores();
  const [pageStep, setPageStep] = useState<PageStep>(PageStep.SEND_CURRENT);
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState<string>("");

  const [memberInfo, setMemberInfo] = useState<LoginInfo>(GetUserProfile());
  const [hiddenMobileNumber, setHiddenMobileNumber] = useState<string>(memberInfo.contact.mobile ? `********${memberInfo.contact.mobile.trim().substring(memberInfo.contact.mobile.length - 3)}` : "NA"); 
  const [hiddenEmailAddress, setHiddenEmailAddress] = useState<string>(`${memberInfo.contact.emailAddress.substring(0, 3)}****@${memberInfo.contact.emailAddress.split("@")[1]}`);
  const [newContactInfo, setNewContactInfo] = useState<string>("");

  const [verificationCode, setVerificationCode] = useState<string>("");

  // CROSS VALIDATION
  const [oldContactVerifyMethod, setOldContactVerifyMethod] = useState<MFAType>((mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) ? MFAType.EMAIL : MFAType.SMS);
  const [newContactVerifyMethod, setNewContactVerifyMethod] = useState<MFAType>((mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) ? MFAType.SMS : MFAType.EMAIL);

  useEffect(() => {
    setOldContactVerifyMethod((mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) ? MFAType.EMAIL : MFAType.SMS)
    setNewContactVerifyMethod((mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) ? MFAType.SMS : MFAType.EMAIL)
  }, [mode]);

  const handleOldContactSendCode = () => {
    const userMobile = GetUserProfile().contact.mobile;
    const userUsername = GetUserProfile().contactId;

    if (!userMobile || !userUsername) return;

    userStore
      .sendMFACode(userMobile, userUsername, oldContactVerifyMethod)
      .then((res: any) => {
        console.log("res", res);
        setPageStep(PageStep.CHECK_CURRENT);
      })
      .catch((err: any) => rootStore.notify(GetErrorInfo(err), 'error'));
  }

  const handleOldContactVerifyCode = () => {
    console.log("handleSubmitVerificationCode");

    userStore
      .checkMFACode(GetUserProfile().mobile, GetUserProfile().username, oldContactVerifyMethod)
      .then((res: any) => {
        console.log("res", res);
        setPageStep(PageStep.ENTER_NEW_CONTACT_INFO)
      })
      .catch((err: any) => rootStore.notify(GetErrorInfo(err), 'error'));
  }

  const handleNewContactSendCode = () => {
    if (newContactInfoIsValid()) {
      userStore
        .sendMFACode(newContactInfo, GetUserProfile().username, newContactVerifyMethod)
        .then((res: any) => {
          console.log("res", res);
          setVerificationCode("");
          setPageStep(PageStep.VERIFY_NEW_CONTACT_INFO)
        })
        .catch((err: any) => rootStore.notify(GetErrorInfo(err), 'error'));
    }
  }

  const handleNewContactVerifyCode = () => {
      userStore
        .sendMFACode(newContactInfo, GetUserProfile().username, newContactVerifyMethod)
        .then((res: any) => {
          console.log("res", res);
          setPageStep(PageStep.UPDATE_FINISHED)
        })
        .catch((err: any) => rootStore.notify(GetErrorInfo(err), 'error'));
  }

  const newContactInfoIsValid = () => {
    if (mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) {
      return !IsEmptyStr(newContactInfo);
    } else {
      return IsValidEmailAddress(newContactInfo);
    }
  }

  return (
    <>
      <Box className="flex flex-col gap-4 ">
        {
          pageStep === PageStep.SEND_CURRENT && <>
            <Typography>A verification code will be sent to your {oldContactVerifyMethod === MFAType.EMAIL ? "email address" : "mobile number"} ({oldContactVerifyMethod === MFAType.EMAIL ? hiddenEmailAddress : hiddenMobileNumber}).</Typography>
            <Box className="flex ">
              <VmButton loading={userStore.sending} onClick={handleOldContactSendCode}>Send Verification Code</VmButton>
            </Box>
          </>
        }

        {
          pageStep === PageStep.CHECK_CURRENT && <>
            <Typography>Please enter the verification code sent to your {oldContactVerifyMethod === MFAType.EMAIL ? "email address" : "mobile number"} ({oldContactVerifyMethod === MFAType.EMAIL ? hiddenEmailAddress : hiddenMobileNumber}).</Typography>
            <TextField fullWidth value={verificationCode} size="small" onChange={(e) => setVerificationCode(e.target.value)} label={`6 digit verification code`} />
            <VmButton loading={userStore.loading} disabled={!verificationCode || IsEmptyStr(verificationCode) || !IsNumeric(verificationCode)} onClick={handleOldContactVerifyCode}>Verify</VmButton>
          </>
        }

        {
          pageStep === PageStep.ENTER_NEW_CONTACT_INFO && <>
            <Typography>Please enter your new {getContactInfoOptionText(mode)}.</Typography>
            <TextField
              fullWidth
              value={newContactInfo}
              size="small"
              onChange={(e) => setNewContactInfo(e.target.value)}
              label={`New ${getContactInfoOptionText(mode)}`}
              InputProps={{
                startAdornment: mode === ContactInfoOption.PHONE_NUMBER
                  ? <Phone sx={{ mr: 1, opacity: 0.4 }} />
                  : mode === ContactInfoOption.EMAIL_ADDRESS
                    ? <Email sx={{ mr: 1, opacity: 0.4 }} />
                    : <PhoneAndroid sx={{ mr: 1, opacity: 0.4 }} />
              }}
            />
            <VmButton loading={userStore.loading} disabled={!newContactInfo || !newContactInfoIsValid()} onClick={handleNewContactSendCode}>
              Verify new {(mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) ? "mobile number" : "email address"}
            </VmButton>
          </>
        }

        {
          pageStep === PageStep.VERIFY_NEW_CONTACT_INFO && <>
            <Typography>Please enter the verification code sent to your {newContactVerifyMethod === MFAType.EMAIL ? "email address" : "mobile number"} ({newContactInfo}).</Typography>
            <TextField fullWidth value={verificationCode} size="small" onChange={(e) => setVerificationCode(e.target.value)} label={`6 digit verification code`} />
            <VmButton loading={userStore.loading} disabled={!verificationCode || IsEmptyStr(verificationCode) || !IsNumeric(verificationCode)} onClick={handleNewContactVerifyCode}>
              Verify new {(mode === ContactInfoOption.MOBILE_NUMBER || mode === ContactInfoOption.PHONE_NUMBER) ? "mobile number" : "email address"}
            </VmButton>
          </>
        }

        {
          pageStep === PageStep.UPDATE_FINISHED && <>
            <Typography>Your contact information has been updated.</Typography>
            <VmButton onClick={onClose}>Done</VmButton>
          </>
        }

      </Box>
    </>
  )
});

export default UpdateContactInfoModal;
